import { useRef } from 'react';
import { navigateToView } from '../registration/routing.js';
import { BookOpenIcon, HandIcon } from '../icons.js';
import { SignInCard } from './SignInCard.js';
import { BrowserNotice } from './BrowserNotice.js';

/**
 * Clicks on the (deliberately muted) learner card before it opens registration.
 * Learner sign-up isn't public yet; the card reads "Coming soon..." and only the
 * team gets through, either by counting to LEARNER_UNLOCK_CLICKS or via the
 * /register-learner URL, which stays open on purpose.
 */
const LEARNER_UNLOCK_CLICKS = 5;

export function LoginView() {
  // A ref, not state: counting must not re-render the card between clicks.
  // It resets on unmount, so the unlock lasts exactly one visit.
  const learnerClicks = useRef(0);

  return (
    <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20 items-start animate-fade-up">
      <div className="pt-2 lg:pt-4">
        <img
          src="/asl-go-logo.avif"
          alt="ASL Go"
          className="w-full max-w-xs mb-7 select-none dark:invert"
        />

        {/* Elevated role choice — two labelled cards routing into the role-specific
            registration wizards. */}
        <div className="rounded-2xl border border-dashed border-ink-300 dark:border-white/12 bg-brand-subtle/40 dark:bg-surface-darkTint p-5 sm:p-6">
          <div className="field-label mb-4">New to ASL Go? Choose how you'll join</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <RoleCard
              icon={BookOpenIcon}
              title="Sign up as a Learner"
              blurb="Coming soon..."
              muted
              onClick={() => {
                learnerClicks.current += 1;
                if (learnerClicks.current >= LEARNER_UNLOCK_CLICKS) navigateToView('register-learner');
              }}
            />
            <RoleCard
              icon={HandIcon}
              title="Apply as a Deaf Guide"
              blurb="Share fluency and earn for your time."
              onClick={() => navigateToView('register-guide')}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-5">
        <SignInCard />
        {/* Last on purpose: on a phone the grid stacks to one column, and a
            learner the iOS app just sent here to buy minutes should meet the
            sign-in card before any "use Chrome" advisory. */}
        <BrowserNotice />
      </div>
    </div>
  );
}

/**
 * `muted` dims the card and drops its hover highlight to read as unavailable. It
 * stays an enabled button on purpose — `disabled` would swallow the unlock clicks,
 * and aria-disabled would tell assistive tech the control is inert when it isn't.
 * The blurb carries the meaning as real text instead.
 */
function RoleCard({
  icon: Icon,
  title,
  blurb,
  onClick,
  muted = false,
}: {
  icon: typeof BookOpenIcon;
  title: string;
  blurb: string;
  onClick: () => void;
  muted?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-xl border border-ink-200 dark:border-white/10 bg-surface-light dark:bg-surface-dark
                 p-4 transition
                 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand dark:focus-visible:ring-brand-bright
                 ${muted ? 'opacity-60' : 'hover:border-brand dark:hover:border-brand-bright'}`}
    >
      <Icon width={26} height={26} className="text-ink-900 dark:text-brand-bright mb-2.5" />
      <div className="font-semibold text-ink-900 dark:text-ink-100">{title}</div>
      <div className="text-[0.8rem] leading-snug text-ink-600 dark:text-[#b6b2c4] mt-0.5">{blurb}</div>
    </button>
  );
}
